/**
 * @module components/Dashboard/RecentActivity
 * @description Feed de actividad reciente del sistema con entradas del trail de auditoría.
 *
 * Muestra las últimas N acciones registradas en `audit_trail` con formato visual:
 * tipo de acción, control afectado, usuario, timestamp relativo y badges de prioridad.
 * Permite filtrar por tipo de acción y expandir cada entrada para ver detalles.
 *
 * @see AuditEntryCard para el renderizado detallado de cada entrada
 * @see DataContext que provee el trail de auditoría
 * @see ActivityBadges para los badges de prioridad y tipo de acción
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Clock, CheckCircle, AlertCircle, Upload, Users, ChevronDown, ChevronUp,
  ArrowRight, TrendingUp, FileText, ExternalLink, X
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { AuditEntry, ISOControl } from '../../types/iso';
import { formatRelativeTime, formatFullDateTime, formatFileSize } from '../../utils/dateUtils';
import { PriorityBadge, ActionTypeBadge } from './ActivityBadges';

type ActivityFilter = 'all' | 'assignment' | 'status_change' | 'evidence_upload' | 'due_date_change' | 'task_created' | 'task_completed';

interface EnrichedActivity extends AuditEntry {
  controlCode: string;
  controlTitle: string;
  controlPriority: ISOControl['priority'];
  controlStatus: ISOControl['status'];
}

interface RecentActivityProps {
  onSelectControl?: (controlId: string) => void;
}

export function RecentActivity({ onSelectControl }: RecentActivityProps = {}) {
  const { filteredControls } = useData();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>('all');

  // Helper function to detect actionType from action text if not defined
  const detectActionType = (action: string, entry: AuditEntry): AuditEntry['actionType'] => {
    // Si ya tiene un actionType definido y no es 'other', úsalo directamente
    if (entry.actionType && entry.actionType !== 'other') {
      return entry.actionType;
    }

    const actionLower = action.toLowerCase();

    // Detectar por palabras clave en el mensaje descriptivo
    if (actionLower.includes('asign') || actionLower.includes('referente')) {
      return 'assignment';
    }
    if (actionLower.includes('cambió el estado') || actionLower.includes('completado') || actionLower.includes('completada')) {
      return 'status_change';
    }
    if (actionLower.includes('subió') || actionLower.includes('evidencia') || actionLower.includes('documento') ||
        actionLower.includes('captura') || actionLower.includes('video') || actionLower.includes('eliminó')) {
      return 'evidence_upload';
    }
    if (actionLower.includes('vencimiento') || actionLower.includes('estableció fecha') ||
        actionLower.includes('modificó la fecha')) {
      return 'due_date_change';
    }
    if (actionLower.includes('creó la tarea') || actionLower.includes('creó tarea')) {
      return 'task_created';
    }
    if (actionLower.includes('completó la tarea')) {
      return 'task_completed';
    }
    if (actionLower.includes('comentario')) {
      return 'comment';
    }

    return entry.actionType || 'other';
  };

  // Generate enriched activity from control data
  const allActivity = useMemo(() => {
    return filteredControls
      .filter(control => control.auditTrail.length > 0)
      .flatMap(control =>
        control.auditTrail.map(entry => {
          const detectedType = detectActionType(entry.action, entry);
          return {
            ...entry,
            actionType: detectedType,
            controlCode: control.code,
            controlTitle: control.title,
            controlPriority: control.priority,
            controlStatus: control.status,
            controlId: control.id
          } as EnrichedActivity & { controlId: string };
        })
      )
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [filteredControls]);

  // Apply filter
  const filteredActivity = useMemo(() => {
    if (activeFilter === 'all') return allActivity;
    return allActivity.filter(activity => activity.actionType === activeFilter);
  }, [allActivity, activeFilter]);

  // Precompute activity counts by type in O(n) — avoids repeated .filter() per tab render
  const countByActionType = useMemo(() => {
    const map = new Map<ActivityFilter, number>([['all', allActivity.length]]);
    for (const a of allActivity) {
      map.set(a.actionType as ActivityFilter, (map.get(a.actionType as ActivityFilter) ?? 0) + 1);
    }
    return map;
  }, [allActivity]);

  const getCountByActionType = useCallback(
    (actionType: ActivityFilter): number => countByActionType.get(actionType) ?? 0,
    [countByActionType]
  );

  // Group activities by control if multiple actions on same control within 1 hour
  // O(n × avg_group_size) instead of O(n²)
  const groupedActivity = useMemo(() => {
    // Pre-bucket activities by controlCode+userId key
    const keyMap = new Map<string, EnrichedActivity[]>();
    for (const activity of filteredActivity) {
      const key = `${activity.controlCode}_${activity.user.id}`;
      if (!keyMap.has(key)) keyMap.set(key, []);
      keyMap.get(key)!.push(activity);
    }

    const groups: { [key: string]: EnrichedActivity[] } = {};
    const ungrouped: EnrichedActivity[] = [];

    for (const activity of filteredActivity) {
      const key = `${activity.controlCode}_${activity.user.id}`;
      const keyActivities = keyMap.get(key)!;
      const activityTime = new Date(activity.timestamp).getTime();

      // Only scan activities with the same key (much smaller set)
      const recentActivities = keyActivities.filter(a =>
        Math.abs(new Date(a.timestamp).getTime() - activityTime) < 3600000 // 1 hour
      );

      if (recentActivities.length > 1) {
        groups[key] = recentActivities;
      } else {
        ungrouped.push(activity);
      }
    }

    // Convert groups to array and merge with ungrouped
    const groupedArray = Object.entries(groups).map(([key, activities]) => ({
      isGroup: true,
      activities,
      key,
      timestamp: activities[0].timestamp
    }));

    const ungroupedArray = ungrouped.map(activity => ({
      isGroup: false,
      activity,
      key: activity.id,
      timestamp: activity.timestamp
    }));

    return [...groupedArray, ...ungroupedArray]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [filteredActivity]);

  // Show only 3 items when collapsed, 20 when expanded
  const displayedActivity = isExpanded ? groupedActivity.slice(0, 20) : groupedActivity.slice(0, 3);

  const toggleItemExpansion = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getActivityIcon = (actionType: string) => {
    switch (actionType) {
      case 'status_change':
      case 'task_completed':
        return CheckCircle;
      case 'evidence_upload':
        return Upload;
      case 'due_date_change':
        return AlertCircle;
      case 'assignment':
        return Users;
      case 'task_created':
        return FileText;
      default:
        return Clock;
    }
  };

  const getActivityColor = (actionType: string) => {
    switch (actionType) {
      case 'status_change':
      case 'task_completed':
        return 'text-green-500 bg-green-50 dark:bg-green-900/20';
      case 'evidence_upload':
        return 'text-blue-500 bg-blue-50 dark:bg-blue-900/20';
      case 'due_date_change':
        return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'assignment':
        return 'text-purple-500 bg-purple-50 dark:bg-purple-900/20';
      case 'task_created':
        return 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20';
      default:
        return 'text-gray-500 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const renderActivityDescription = (activity: EnrichedActivity) => {
    // Usar directamente el mensaje descriptivo mejorado que ya viene de DataContext
    // Solo agregar el código del control al final para contexto
    return (
      <>
        <span className="font-medium text-gray-900 dark:text-white">
          {activity.user.name}
        </span>
        {' '}
        <span className="lowercase-first-letter">
          {activity.action.charAt(0).toLowerCase() + activity.action.slice(1)}
        </span>
        {' en '}
        <span className="font-medium text-blue-600 dark:text-blue-400">
          {activity.controlCode}
        </span>
      </>
    );
  };

  const renderExpandedDetails = (activity: EnrichedActivity) => {
    return (
      <div className="mt-2 pl-7 space-y-2 text-xs">
        {/* Files uploaded */}
        {activity.filesUploaded && activity.filesUploaded.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2 space-y-1">
            <div className="font-medium text-gray-700 dark:text-gray-300">Archivos:</div>
            {activity.filesUploaded.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {file.name}
                </span>
                <span className="text-gray-500">{formatFileSize(file.size)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Impact metrics */}
        {activity.impactMetrics && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2 space-y-1">
            {activity.impactMetrics.completionChange !== undefined && (
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <TrendingUp className="w-3 h-3" />
                <span>
                  {activity.impactMetrics.completionChange > 0 ? '+' : ''}
                  {activity.impactMetrics.completionChange}% cumplimiento
                </span>
              </div>
            )}
            {activity.impactMetrics.controlProgress !== undefined && (
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <span>Control ahora está {activity.impactMetrics.controlProgress}% completo</span>
              </div>
            )}
          </div>
        )}

        {/* Full timestamp */}
        <div className="text-gray-500 dark:text-gray-400">
          Fecha completa: {formatFullDateTime(activity.timestamp)}
        </div>
      </div>
    );
  };

  const renderGroupedActivity = (activities: (EnrichedActivity & { controlId: string })[], key: string) => {
    const firstActivity = activities[0];
    const isExpanded = expandedItems.has(key);
    const IconComponent = Users;

    return (
      <div key={key} className="border-l-2 border-purple-300 dark:border-purple-700 pl-3">
        <div className="flex items-start space-x-3">
          <div className="p-1.5 rounded-full text-purple-500 bg-purple-50 dark:bg-purple-900/20">
            <IconComponent className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-gray-900 dark:text-white">
                <span className="font-medium">{firstActivity.user.name}</span>
                {' '}realizó{' '}
                <span className="font-semibold text-purple-600 dark:text-purple-400">
                  {activities.length} acciones
                </span>
                {' '}en{' '}
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {firstActivity.controlCode}
                </span>
              </p>
              <PriorityBadge priority={firstActivity.controlPriority} />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {firstActivity.controlTitle}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatRelativeTime(firstActivity.timestamp)}
              </p>
              {onSelectControl && (
                <button
                  onClick={() => handleViewControl(firstActivity)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 transition-colors"
                  title={`Ver detalles de ${firstActivity.controlCode}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  Ver control
                </button>
              )}
              <button
                onClick={() => toggleItemExpansion(key)}
                className="text-xs text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Ocultar detalles
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Ver detalles
                  </>
                )}
              </button>
            </div>

            {isExpanded && (
              <div className="mt-3 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                {activities.map(activity => {
                  const Icon = getActivityIcon(activity.actionType);
                  const colorClass = getActivityColor(activity.actionType);

                  return (
                    <div key={activity.id} className="text-xs bg-gray-50 dark:bg-gray-900/50 rounded p-2">
                      <div className="flex items-start gap-2">
                        <div className={`p-1 rounded ${colorClass} flex-shrink-0`}>
                          <Icon className="w-3 h-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-700 dark:text-gray-300 mb-1">
                            {activity.action}
                          </div>
                          <span className="text-gray-500 dark:text-gray-400">
                            {formatRelativeTime(activity.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleViewControl = (activity: EnrichedActivity & { controlId: string }) => {
    if (onSelectControl) {
      onSelectControl(activity.controlId);
    }
  };

  const renderSingleActivity = (activity: EnrichedActivity & { controlId: string }) => {
    const isExpanded = expandedItems.has(activity.id);
    const IconComponent = getActivityIcon(activity.actionType);
    const colorClass = getActivityColor(activity.actionType);

    return (
      <div key={activity.id} className="flex items-start space-x-3">
        <div className={`p-1.5 rounded-full ${colorClass}`}>
          <IconComponent className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-gray-900 dark:text-white">
              {renderActivityDescription(activity)}
            </p>
            <PriorityBadge priority={activity.controlPriority} />
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
            {activity.controlTitle}
          </p>

          <div className="flex items-center gap-3 mt-1.5">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatRelativeTime(activity.timestamp)}
            </p>

            {/* Impact metrics inline */}
            {activity.impactMetrics?.completionChange !== undefined && (
              <span className="text-xs flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                <TrendingUp className="w-3 h-3" />
                {activity.impactMetrics.completionChange > 0 ? '+' : ''}
                {activity.impactMetrics.completionChange}%
              </span>
            )}

            {/* Quick actions */}
            <div className="flex items-center gap-2">
              {onSelectControl && (
                <button
                  onClick={() => handleViewControl(activity)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 transition-colors"
                  title={`Ver detalles de ${activity.controlCode}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  Ver control
                </button>
              )}

              {(activity.filesUploaded?.length || activity.impactMetrics) && (
                <button
                  onClick={() => toggleItemExpansion(activity.id)}
                  className="text-xs text-gray-600 dark:text-gray-400 hover:underline flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      Menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      Más detalles
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {isExpanded && renderExpandedDetails(activity)}
        </div>
      </div>
    );
  };

  const filterButtons: { value: ActivityFilter; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { value: 'all', label: 'Todas', icon: Clock },
    { value: 'assignment', label: 'Asignaciones', icon: Users },
    { value: 'status_change', label: 'Cambios Estado', icon: CheckCircle },
    { value: 'evidence_upload', label: 'Evidencias', icon: Upload },
    { value: 'task_completed', label: 'Tareas Completadas', icon: CheckCircle }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Actividad Reciente
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Últimas acciones en controles ISO
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-400" />
        </div>
      </div>

      {/* Filter buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        {filterButtons.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            onClick={() => setActiveFilter(value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              activeFilter === value
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span className="ml-1 px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded text-xs">
              {getCountByActionType(value)}
            </span>
          </button>
        ))}
        {activeFilter !== 'all' && (
          <button
            onClick={() => setActiveFilter('all')}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="w-3.5 h-3.5" />
            Limpiar
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-[340px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
        {filteredActivity.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeFilter === 'all'
                ? 'No hay actividad reciente'
                : 'No hay actividad de este tipo'
              }
            </p>
          </div>
        ) : (
          displayedActivity.map((item: any) => {
            if (item.isGroup) {
              return renderGroupedActivity(item.activities, item.key);
            } else {
              return renderSingleActivity(item.activity);
            }
          })
        )}
      </div>

      {filteredActivity.length > 3 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Mostrar menos
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Ver más actividad ({filteredActivity.length - 3} más)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
